import React from 'react';
import { message as staticMessage, App as AntdApp, Button } from 'antd';

/**
 * Wraps an Ant Design message API's .error() method so that:
 * 1. Error toasts do NOT auto-dismiss (duration: 0).
 * 2. An "OK" button is displayed inside the toast.
 * 3. The error toast is only removed when the user clicks "OK".
 */
export function wrapMessageError(messageApi) {
  if (!messageApi || messageApi._isErrorToastWrapped) {
    return messageApi;
  }

  const originalError = messageApi.error;

  messageApi.error = function (content, duration, onClose) {
    let key;
    let text;
    let userOptions = {};

    if (typeof content === 'object' && content !== null && !React.isValidElement(content)) {
      userOptions = { ...content };
      text = userOptions.content;
      key = userOptions.key || `err-toast-${Date.now()}-${Math.random()}`;
    } else {
      text = content;
      key = `err-toast-${Date.now()}-${Math.random()}`;
    }

    const handleClose = (e) => {
      e?.stopPropagation?.();
      messageApi.destroy(key);
      if (typeof userOptions.onClose === 'function') {
        userOptions.onClose();
      } else if (typeof onClose === 'function') {
        onClose();
      }
    };

    const formattedContent = (
      <div style={{ display: 'inline-flex', alignItems: 'center', gap: '12px' }}>
        <span>{text}</span>
        <Button
          type="primary"
          size="small"
          onClick={handleClose}
          style={{ fontWeight: 500, borderRadius: '4px' }}
        >
          OK
        </Button>
      </div>
    );

    return originalError.call(messageApi, {
      ...userOptions,
      key,
      content: formattedContent,
      duration: 0, // 0 means it will not auto-dismiss
    });
  };

  messageApi._isErrorToastWrapped = true;
  return messageApi;
}

// 1. Wrap static message object imported from 'antd'
wrapMessageError(staticMessage);

// 2. Wrap App.useApp() hook from 'antd'
const originalUseApp = AntdApp.useApp;
AntdApp.useApp = function () {
  const app = originalUseApp();
  if (app && app.message) {
    wrapMessageError(app.message);
  }
  return app;
};
